import { useEffect, useState } from 'react';
import { ScheduledEventPreviewInput, ScheduledEventRunDatePreview } from '../../store/types';
import { DateTime } from 'luxon';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { Props } from '../container';

interface RunDatePreviewProps {
  input: ScheduledEventPreviewInput;
  previewScheduledEventRunDates: Props['previewScheduledEventRunDates'];
}

export const RunDatePreview = ({ input, previewScheduledEventRunDates }: RunDatePreviewProps): JSX.Element => {
  const [preview, setPreview] = useState<ScheduledEventRunDatePreview | null>(null);
  const [status, setStatus] = useState<'loading' | 'idle' | 'error'>('loading');

  const inputKey = JSON.stringify(input);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    const handle = setTimeout(() => {
      previewScheduledEventRunDates(input)
        .unwrap()
        .then((res) => {
          if (!cancelled) {
            setPreview(res);
            setStatus('idle');
          }
        })
        .catch(() => {
          if (!cancelled) {
            setStatus('error');
          }
        });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [inputKey]);

  const formatRunDate = (value: Date | null | undefined, format: string): string | null =>
    value ? DateTime.fromJSDate(new Date(value)).toFormat(format) : null;

  const nextRun = formatRunDate(preview?.nextRunOn, 'cccc, d LLLL yyyy');
  const finalRun = formatRunDate(preview?.finalRunOn, 'dd LLLL yyyy');

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Preview
      </Typography>
      {!preview && status === 'loading' && <Typography variant="body2">Calculating run dates…</Typography>}
      {status === 'error' && (
        <Typography variant="body2" color="error">
          Unable to calculate run dates.
        </Typography>
      )}
      {preview && status !== 'error' && (
        <>
          <Typography variant="body2">{nextRun ? `Next Run: ${nextRun}` : 'No upcoming run'}</Typography>
          {preview.remainingOccurrences > 0 && finalRun && (
            <Typography variant="body2">
              {preview.remainingOccurrences} remaining occurrence(s). Final run on {finalRun}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};
